import { ReactNode, useState } from "react";
import { queryConfig } from "@/lib/query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toast";
import { Modal } from "@/components/ui/modal";
import { IntercomProvider } from "react-use-intercom";
import ENV from "@/utils/env";
import { Error } from "@/components/fallbacks/error-boundaries";
import { SegmentInitializer } from "@/lib/segment-analytics";
import { ErrorBoundary } from "@sentry/react";
import { SentryUserInitializer } from "@/lib/sentry-user-init";
import { PermissionsProvider } from "@/features/auth/components/permissions-provider";
import { StudioPostHogProvider } from "@/lib/posthog-provider";
import { AutoRefreshManager } from "@/features/version/components/auto-refresh-manager";

/* Prop Types */
type AppProviderProps = {
  children: ReactNode;
};

/*
 * AppProvider component
 * This contains all the providers for the application
 */
export const AppProvider = ({ children }: AppProviderProps) => {
  /* Queries */
  const [queryClient] = useState(() => new QueryClient({ defaultOptions: queryConfig }));

  /* Render */
  return (
    <ErrorBoundary fallback={({ error, resetError }) => <Error error={error} resetError={resetError} />}>
      <StudioPostHogProvider>
        <QueryClientProvider client={queryClient}>
          {import.meta.env.DEV && <ReactQueryDevtools />}
          <AutoRefreshManager />
          {children}
          <Toaster />
          <Modal />
        </QueryClientProvider>
      </StudioPostHogProvider>
    </ErrorBoundary>
  );
};

/*
 * AuthenticatedProviders component
 *
 * Providers here depend on an authenticated session and should only be mounted
 * for protected routes.
 */
export const AuthenticatedProviders = ({ children }: AppProviderProps) => {
  return (
    <IntercomProvider appId={ENV.INTERCOM_APP_ID} autoBoot>
      <PermissionsProvider>
        <SentryUserInitializer />
        <SegmentInitializer>{children}</SegmentInitializer>
      </PermissionsProvider>
    </IntercomProvider>
  );
};
